/**
 * Nerd detail — tabs: Perfil + Chamados + Faturas.
 */
import { useQuery } from "@tanstack/react-query";
import { Briefcase, Star } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { DetailCard, DetailLayout, KeyValue } from "@/components/detail-layout";
import { EntityActions } from "@/components/entity-actions";
import { NerdSkillsEditor } from "@/components/nerd-skills-editor";
import { JobStatusPill, NerdStatusPill } from "@/components/status-pill";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { nerdsApi } from "@/lib/api-cadastros";
import { invoicesApi, jobsApi } from "@/lib/api-jobs";
import { formatCPF, formatCurrency, formatDate, formatRelative } from "@/lib/format";

export function NerdDetailPage() {
  const { id = "" } = useParams();
  const nerd = useQuery({ queryKey: ["nerd", id], queryFn: () => nerdsApi.get(id), enabled: !!id });
  const jobs = useQuery({
    queryKey: ["nerd-jobs", id],
    queryFn: () => jobsApi.list({ assigned_nerd_id: id, size: 50 }),
    enabled: !!id,
  });
  const invoices = useQuery({
    queryKey: ["nerd-invoices", id],
    queryFn: () => invoicesApi.list({ nerd_id: id, size: 50 }),
    enabled: !!id,
  });

  if (!nerd.data) return <div className="text-muted-foreground py-10">Carregando…</div>;
  const n = nerd.data;

  return (
    <DetailLayout
      backTo="/nerds"
      eyebrow="Técnico"
      visual={
        <Avatar size="xl">
          <AvatarFallback name={n.display_name ?? n.legal_name ?? n.user_name ?? n.user_email ?? ""} />
        </Avatar>
      }
      title={n.display_name ?? n.legal_name ?? n.user_name ?? n.user_email ?? "Sem nome"}
      subtitle={
        <span>
          {n.user_email && <span>{n.user_email}</span>}
          {(n.cpf || n.cnpj) && (
            <span className="font-mono ml-2">· {formatCPF(n.cpf) ?? n.cnpj}</span>
          )}
        </span>
      }
      badges={
        <>
          <NerdStatusPill status={n.status} />
          <Badge variant="default" className="font-mono">
            <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> {n.rank_score}/100
          </Badge>
          <Badge variant="muted">{n.completed_jobs} jobs concluídos</Badge>
        </>
      }
      actions={
        <EntityActions
          onDelete={() => nerdsApi.remove(id)}
          redirectAfterDelete="/nerds"
          invalidateQueries={["nerds"]}
          resourceLabel="técnico"
          hideEdit
        />
      }
    >
      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Perfil</TabsTrigger>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="jobs">Chamados ({jobs.data?.total ?? 0})</TabsTrigger>
          <TabsTrigger value="invoices">Faturas ({invoices.data?.total ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailCard title="Identificação">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <KeyValue label="Nome legal" value={n.legal_name} />
                <KeyValue label="CPF" value={<span className="font-mono">{formatCPF(n.cpf)}</span>} />
                <KeyValue label="CNPJ" value={n.cnpj ?? "—"} />
                <KeyValue label="Status" value={<NerdStatusPill status={n.status} />} />
              </div>
            </DetailCard>
            <DetailCard title="Atendimento">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <KeyValue label="Raio de atuação" value={`${n.working_radius_km} km`} />
                <KeyValue label="Jobs concluídos" value={String(n.completed_jobs)} />
                <KeyValue label="Rank score" value={`${n.rank_score}/100`} />
                <KeyValue label="Cidade base" value={`${n.address?.cidade?.name ?? "—"}/${n.address?.cidade?.uf ?? ""}`} />
              </div>
            </DetailCard>
            {n.bio && (
              <DetailCard title="Bio" className="md:col-span-2">
                <p className="text-sm whitespace-pre-wrap">{n.bio}</p>
              </DetailCard>
            )}
          </div>
        </TabsContent>

        <TabsContent value="skills">
          <NerdSkillsEditor nerdId={n.id} />
        </TabsContent>

        <TabsContent value="jobs">
          {jobs.data && jobs.data.items.length > 0 ? (
            <DetailCard>
              <div className="divide-y divide-slate-200/40 dark:divide-white/5 -m-6">
                {jobs.data.items.map((j) => (
                  <Link
                    key={j.id}
                    to={`/jobs/${j.id}`}
                    className="flex items-center justify-between gap-4 px-6 py-3.5 hover:bg-slate-50/60 dark:hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="min-w-0">
                      <span className="font-mono text-xs text-muted-foreground">{j.code}</span>
                      <p className="font-medium truncate">{j.title}</p>
                      <p className="text-xs text-muted-foreground">{formatRelative(j.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold tabular-nums">{formatCurrency(j.price)}</span>
                      <JobStatusPill status={j.status} />
                    </div>
                  </Link>
                ))}
              </div>
            </DetailCard>
          ) : (
            <EmptyState icon={Briefcase} title="Sem chamados atribuídos" />
          )}
        </TabsContent>

        <TabsContent value="invoices">
          {invoices.data && invoices.data.items.length > 0 ? (
            <DetailCard>
              <div className="divide-y divide-slate-200/40 dark:divide-white/5 -m-6">
                {invoices.data.items.map((inv) => (
                  <Link
                    key={inv.id}
                    to={`/invoices/${inv.id}`}
                    className="flex items-center justify-between gap-4 px-6 py-3.5 hover:bg-slate-50/60 dark:hover:bg-white/[0.03] transition-colors"
                  >
                    <div>
                      <span className="font-mono text-sm font-medium">{inv.code}</span>
                      <p className="text-xs text-muted-foreground">
                        Pagamento: {formatDate(inv.scheduled_payment_date)}
                      </p>
                    </div>
                    <span className="font-semibold tabular-nums">{formatCurrency(inv.total)}</span>
                  </Link>
                ))}
              </div>
            </DetailCard>
          ) : (
            <EmptyState title="Sem faturas" />
          )}
        </TabsContent>
      </Tabs>
    </DetailLayout>
  );
}
